/**
 * The work reset (Jeremy, 22 Oct 2026): empties every request, deployment, WFH day and the outbox,
 * and keeps people, payroll balances, holidays, the NTD list, problem reports and the admin log
 * (api/src/hr/work-reset.ts). For resetting production between training sessions.
 *
 * Shown only while the API says the switch is on (ALLOW_WORK_RESET on the App Service, set on the
 * day and removed after). A face, no rules: the API re-checks the switch, HR, acting-as and the
 * typed text on the POST.
 */
import { useCallback, useEffect, useState } from "react";

import { ApiError, useSchedulerApi, type WorkCounts } from "../api/scheduler";
import { useRefreshOnReturn } from "../refreshOnReturn";

const CONFIRM = "WIPE ALL WORK";

/** Each work table in words. A table the API adds before this list knows it shows by name. */
const LABEL: Record<string, string> = {
  request: "Requests (PTO, Time Off, NTD, old field requests)",
  request_day: "Request days",
  request_resource: "People on requests",
  deployment: "Field deployments",
  deployment_crew: "Deployment crew places",
  deployment_event: "Deployment history entries",
  deployment_note: "Deployment notes",
  wfh_day: "Work-from-home days",
  notification_outbox: "Notifications waiting to be sent",
};

const total = (c: WorkCounts) => Object.values(c).reduce((a, b) => a + b, 0);

export function WorkReset() {
  const api = useSchedulerApi();
  const [counts, setCounts] = useState<WorkCounts | null>(null);
  const [canRefill, setCanRefill] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ before: WorkCounts; after: WorkCounts; demoSeedCanRefill: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    api.workResetStatus().then((s) => {
      setCounts(s.enabled ? s.counts : null);
      setCanRefill(s.demoSeedCanRefill);
    }, () => setCounts(null));
  }, [api]);
  // Read again quietly on coming back to the tab (punch list F172).
  const back = useRefreshOnReturn();
  useEffect(refresh, [refresh, back]);

  if (!counts) return null;

  async function reset() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api.workReset(typed));
      setTyped("");
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "The work data could not be reset.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad">
      <h3>Reset the work data</h3>
      <p className="hint">
        Deletes every request (PTO, every Time Off kind, no-travel days), every field deployment with
        its crew, notes and history, every work-from-home day, and notifications not yet sent. For
        clearing training sessions. <strong>Kept:</strong> people, payroll balances and their uploads,
        holidays, the NTD "also notify" list, problem reports and the admin log, which records this
        reset under your name. It cannot be undone.
      </p>
      <p className="hint">
        {canRefill
          ? "On this environment the demo seed (SEED_DEMO_ON_START) could add invented bookings at the next restart, if it is set."
          : "Nothing refills it afterwards: the demo seed never runs on production, whatever its setting says."}
      </p>
      <table className="data">
        <thead><tr><th>Would be deleted</th><th className="r">Rows</th></tr></thead>
        <tbody>
          {Object.entries(counts).map(([table, n]) => (
            <tr key={table}><td>{LABEL[table] ?? table}</td><td className="r">{n}</td></tr>
          ))}
          <tr><td><strong>Total</strong></td><td className="r"><strong>{total(counts)}</strong></td></tr>
        </tbody>
      </table>
      <label className="field" style={{ marginTop: 15 }}>
        <span className="label">Type {CONFIRM} to confirm</span>
        <input
          type="text"
          aria-label={`Type ${CONFIRM} to confirm`}
          autoComplete="off"
          spellCheck={false}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
      </label>
      {error ? <p className="error" role="alert">{error}</p> : null}
      <button type="button" className="primary danger" disabled={busy || typed !== CONFIRM} onClick={() => void reset()}>
        {busy ? "Resetting…" : "Reset the work data"}
      </button>
      {result ? (
        <div className="toast" role="status" style={{ marginTop: 15 }}>
          <p>
            Reset. {total(result.before)} rows deleted; {total(result.after)} left.
            {result.demoSeedCanRefill ? "" : " The demo seed cannot refill it: it never runs on production."}
          </p>
          <table className="data">
            <thead><tr><th>Table</th><th className="r">Before</th><th className="r">After</th></tr></thead>
            <tbody>
              {Object.entries(result.before).map(([table, n]) => (
                <tr key={table}>
                  <td>{LABEL[table] ?? table}</td><td className="r">{n}</td><td className="r">{result.after[table] ?? 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
